/**
 * Request Deduplicator - Prevents duplicate concurrent requests
 * 
 * When multiple components request the same resource simultaneously,
 * this utility ensures only ONE request is made, and all callers share the result.
 * 
 * Usage:
 *   const data = await dedupedFetch('panels:myGuildId', () => fetchPanelsDirectly(guildId));
 */

const pendingRequests = new Map<string, Promise<any>>();

/**
 * Deduplicates concurrent requests using the same key
 * @param key - Unique identifier for this request (e.g., "panels:12345")
 * @param fetchFn - Async function that performs the actual request
 * @returns Promise that resolves with the data (shared across all callers)
 */
export async function dedupedFetch<T>(
  key: string,
  fetchFn: () => Promise<T>
): Promise<T> {
  // Check if this request is already in-flight
  if (pendingRequests.has(key)) {
    return pendingRequests.get(key)! as Promise<T>;
  }

  // Create the request promise and store it
  const promise = fetchFn()
    .finally(() => {
      // Clean up after request completes (success or error)
      pendingRequests.delete(key);
    });

  pendingRequests.set(key, promise);
  return promise;
}

/**
 * Clear a pending request from the deduplication cache
 * Useful when you need to force a refresh
 */
export function clearDedupedRequest(key: string): void {
  pendingRequests.delete(key);
}

/**
 * Clear all pending requests
 * Use sparingly - mainly for testing or on logout
 */
export function clearAllDedupedRequests(): void {
  pendingRequests.clear();
}
